import type {
  ContactWorkbenchListFilters,
  ContactWorkbenchOrganizationOption,
  ContactWorkbenchOwnerOption,
} from "../types";
import { isHttpClientError, resolveReadableHttpErrorMessage } from "../../../lib/http/errors";

const CHINESE_TEXT_PATTERN = /[\u3400-\u9fff]/;
const SCHOOL_BATCH_ASSIGNMENT_CITY_KEY_SEPARATOR = "\u001f";

type SchoolBatchAssignmentCityOption = {
  value: string;
  label: string;
};

/** 合并学校筛选候选，保留已选学校标签在远程搜索结果变化时不丢名。 */
export function mergeContactFilterSchoolOptions(
  nextOptions: ContactWorkbenchOrganizationOption[],
  selectedIds: number[],
  previousOptions: ContactWorkbenchOrganizationOption[],
): ContactWorkbenchOrganizationOption[] {
  const byId = new Map<number, ContactWorkbenchOrganizationOption>();
  for (const option of nextOptions) {
    byId.set(option.id, option);
  }
  const previousById = new Map(previousOptions.map((option) => [option.id, option]));
  for (const id of selectedIds) {
    if (byId.has(id)) {
      continue;
    }
    const previous = previousById.get(id);
    if (previous) {
      byId.set(id, previous);
    }
  }
  return [...byId.values()];
}

/** 用 URL/草稿中的学校 ID 与名称种子化下拉选项，避免只显示数字。 */
export function buildContactFilterSchoolOptionsFromSelection(
  orgIds?: number[],
  orgNames?: string[],
): ContactWorkbenchOrganizationOption[] {
  if (!orgIds?.length) {
    return [];
  }
  return orgIds.map((id, index) => {
    const name = orgNames?.[index]?.trim();
    return {
      id,
      orgName: name || "已选学校",
    };
  });
}

/** 按已选学校 ID 从候选中抽出名称，与 ID 等长对齐。 */
export function resolveContactFilterOrgNames(
  orgIds: number[] | undefined,
  options: ContactWorkbenchOrganizationOption[],
  previousNames?: string[],
): string[] | undefined {
  if (!orgIds?.length) {
    return undefined;
  }
  const byId = new Map(options.map((option) => [option.id, option.orgName]));
  return orgIds.map((id, index) => {
    const fromOptions = byId.get(id)?.trim();
    if (fromOptions) {
      return fromOptions;
    }
    const previous = previousNames?.[index]?.trim();
    return previous || "已选学校";
  });
}

/** 归一化联系人工作台筛选条件。 */
export function normalizeContactWorkbenchFilters(
  filters: ContactWorkbenchListFilters,
): ContactWorkbenchListFilters {
  const provincesFromMulti = normalizeOptionalTexts(filters.provinces);
  const legacyProvince = normalizeOptionalText(filters.province);
  const provinces = provincesFromMulti ?? (legacyProvince ? [legacyProvince] : undefined);
  const orgIdsFromMulti = normalizePositiveIds(filters.orgIds);
  const legacyOrgId = filters.orgId && filters.orgId > 0 ? filters.orgId : undefined;
  const orgIds = orgIdsFromMulti ?? (legacyOrgId ? [legacyOrgId] : undefined);
  const orgNames = orgIds
    ? orgIds.map((_, index) => normalizeOptionalText(filters.orgNames?.[index]) ?? "已选学校")
    : undefined;
  const ownerUserIds =
    normalizePositiveIds(filters.ownerUserIds) ??
    (filters.ownerUserId && filters.ownerUserId > 0 ? [filters.ownerUserId] : undefined);
  return {
    viewScope: filters.viewScope ?? "mine",
    contactStar: normalizeOptionalText(filters.contactStar),
    contactStarUnset: filters.contactStarUnset ?? false,
    recordLabels: normalizeOptionalTexts(filters.recordLabels),
    followUpLabels: normalizeOptionalTexts(filters.followUpLabels),
    keyword: normalizeOptionalText(filters.keyword),
    ownerUserIds,
    sharedOwnerUserIds: normalizePositiveIds(filters.sharedOwnerUserIds),
    temporaryHandlerUserIds: normalizePositiveIds(filters.temporaryHandlerUserIds),
    orgIds,
    orgNames,
    provinces,
    city: normalizeOptionalText(filters.city),
    district: normalizeOptionalText(filters.district),
    isActive: filters.isActive,
    nlQuery: normalizeOptionalText(filters.nlQuery),
  };
}

/** 格式化联系人归属销售名称。 */
export function formatContactOwners(names: string[]) {
  return names.length ? names.join("、") : "未分配";
}

/** 按姓名模糊收窄归属销售筛选候选。 */
export function filterOwnerOptionsByKeyword(
  options: ContactWorkbenchOwnerOption[],
  keyword: string,
) {
  const normalized = keyword.trim().toLowerCase();
  if (!normalized) {
    return options;
  }
  return options.filter((option) => option.userName.toLowerCase().includes(normalized));
}

/** 合并地址展示文本。 */
export function formatContactArea(province?: string, city?: string, district?: string) {
  return [province, city, district].filter(Boolean).join(" / ") || "-";
}

/** 只透传可读的中文业务错误，其余技术异常使用当前操作的中文兜底提示。 */
export function formatContactWorkbenchError(
  error: unknown,
  fallback: string,
  options?: { timeoutMessage?: string },
) {
  if (
    isHttpClientError(error) &&
    error.code === "CRM_COLLABORATION_TASK_ALREADY_PUBLISHED"
  ) {
    return "已投放任务池";
  }
  const message = error instanceof Error ? error.message.trim() : "";
  if (/timeout/i.test(message) || /timed out/i.test(message) || /ECONNABORTED/i.test(message)) {
    return options?.timeoutMessage ?? "操作超时，请缩小学校范围后重试，或稍后重试";
  }
  const resolved = resolveReadableHttpErrorMessage(error, fallback, {
    validationFallback: "请完整填写联系人必填项后再保存。",
  });
  if (resolved !== fallback) {
    return resolved;
  }
  return message && CHINESE_TEXT_PATTERN.test(message) ? message : fallback;
}

/** 生成按学校批量分配的省份与城市候选，城市键包含省份以区分同名城市。 */
export function buildSchoolBatchAssignmentRegionOptions(
  schools: ContactWorkbenchOrganizationOption[],
): { provinces: string[]; cities: SchoolBatchAssignmentCityOption[] } {
  const provinces = new Set<string>();
  const cities = new Map<string, SchoolBatchAssignmentCityOption>();
  schools.forEach((school) => {
    const province = school.province?.trim();
    const city = school.city?.trim();
    if (province) {
      provinces.add(province);
    }
    if (province && city) {
      const value = createSchoolBatchAssignmentCityKey(province, city);
      cities.set(value, { value, label: `${province} / ${city}` });
    }
  });
  return {
    provinces: Array.from(provinces).sort((left, right) => left.localeCompare(right, "zh-CN")),
    cities: Array.from(cities.values()).sort((left, right) =>
      left.label.localeCompare(right.label, "zh-CN"),
    ),
  };
}

/** 按已选省份与城市的并集筛选学校，结果沿用完整区域学校范围顺序。 */
export function filterSchoolsByBatchAssignmentRegions(
  schools: ContactWorkbenchOrganizationOption[],
  selectedProvinces: string[],
  selectedCityKeys: string[],
): ContactWorkbenchOrganizationOption[] {
  const provinceSet = new Set(selectedProvinces.map((item) => item.trim()).filter(Boolean));
  const citySet = new Set(selectedCityKeys.filter(Boolean));
  const seenIds = new Set<number>();
  return schools.filter((school) => {
    if (seenIds.has(school.id)) return false;
    const province = school.province?.trim();
    const city = school.city?.trim();
    const matched =
      Boolean(province && provinceSet.has(province)) ||
      Boolean(province && city && citySet.has(createSchoolBatchAssignmentCityKey(province, city)));
    if (matched) seenIds.add(school.id);
    return matched;
  });
}

/** 生成稳定且不会混淆跨省同名城市的城市选择键。 */
function createSchoolBatchAssignmentCityKey(province: string, city: string) {
  return `${province}${SCHOOL_BATCH_ASSIGNMENT_CITY_KEY_SEPARATOR}${city}`;
}

/** 归一化可选筛选文本。 */
function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

/** 归一化可选多值文本筛选并按原顺序去重。 */
function normalizeOptionalTexts(values?: string[]) {
  const normalized = Array.from(
    new Set((values ?? []).map((value) => value.trim()).filter(Boolean)),
  );
  return normalized.length ? normalized : undefined;
}

/** 归一化正整数 ID 多选并按原顺序去重。 */
function normalizePositiveIds(values?: number[]) {
  const normalized: number[] = [];
  for (const value of values ?? []) {
    if (!Number.isSafeInteger(value) || value < 1 || normalized.includes(value)) {
      continue;
    }
    normalized.push(value);
  }
  return normalized.length ? normalized : undefined;
}

/** 将列表时间格式化为 YYYY-MM-DD HH时，历史空值统一显示短横线。 */
export function formatContactWorkbenchHour(value?: string) {
  if (!value) return "-";
  const match = value.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2})/);
  return match ? `${match[1]} ${match[2]}时` : "-";
}

/** 格式化联系人星级，未设置时给出明确文案。 */
export function formatContactStar(value?: string) {
  return value?.trim() || "未设置";
}

/** 拆分粘贴到学校筛的校名文本，支持换行/逗号/顿号/分号。 */
export function splitPastedSchoolNames(raw: string): string[] {
  const names: string[] = [];
  for (const part of raw.split(/[\n\r\t,，、;；]+/)) {
    const name = part.trim();
    if (!name || names.includes(name)) {
      continue;
    }
    names.push(name);
  }
  return names;
}

/** 归一化校名便于忽略空白差异做精确比对。 */
function normalizeSchoolNameKey(value: string) {
  return value.replace(/\s+/g, "");
}

/** 在本地学校候选中按粘贴校名精确匹配。 */
export function matchOrganizationsByExactNames(
  names: string[],
  options: ContactWorkbenchOrganizationOption[],
): {
  matched: ContactWorkbenchOrganizationOption[];
  missing: string[];
} {
  const byExact = new Map<string, ContactWorkbenchOrganizationOption>();
  const byNormalized = new Map<string, ContactWorkbenchOrganizationOption[]>();
  for (const option of options) {
    const exact = option.orgName.trim();
    if (exact && !byExact.has(exact)) {
      byExact.set(exact, option);
    }
    const key = normalizeSchoolNameKey(exact);
    if (!key) {
      continue;
    }
    const bucket = byNormalized.get(key) ?? [];
    bucket.push(option);
    byNormalized.set(key, bucket);
  }
  const matched: ContactWorkbenchOrganizationOption[] = [];
  const missing: string[] = [];
  const seenIds = new Set<number>();
  for (const name of names) {
    const exact = byExact.get(name);
    const normalizedHits = byNormalized.get(normalizeSchoolNameKey(name)) ?? [];
    const hit =
      exact ??
      (normalizedHits.length === 1 ? normalizedHits[0] : undefined);
    if (!hit) {
      missing.push(name);
      continue;
    }
    if (seenIds.has(hit.id)) {
      continue;
    }
    seenIds.add(hit.id);
    matched.push(hit);
  }
  return { matched, missing };
}

/** 从远程搜索候选中挑选与粘贴校名精确一致的学校。 */
export function pickBestOrganizationMatch(
  name: string,
  candidates: ContactWorkbenchOrganizationOption[],
): ContactWorkbenchOrganizationOption | undefined {
  const trimmed = name.trim();
  if (!trimmed) {
    return undefined;
  }
  const exact = candidates.find((item) => item.orgName.trim() === trimmed);
  if (exact) {
    return exact;
  }
  const key = normalizeSchoolNameKey(trimmed);
  const normalizedHits = candidates.filter(
    (item) => normalizeSchoolNameKey(item.orgName) === key,
  );
  return normalizedHits.length === 1 ? normalizedHits[0] : undefined;
}
